import * as onnx from "onnxruntime-web";
import { applyIsotonicCalibrationBatch } from "./isotonicCalibration";

export const DEFAULT_DELAY_QUERIES = [
  { label: "1m", hours: 1 / 60, reviews: 0 },
  { label: "2m", hours: 2 / 60, reviews: 0 },
  { label: "3m", hours: 3 / 60, reviews: 0 },
  { label: "5m", hours: 5 / 60, reviews: 0 },
  { label: "10m", hours: 1 / 6, reviews: 0 },
  { label: "1h", hours: 1, reviews: 0 },
  { label: "1d", hours: 24, reviews: 0 },
  { label: "3d", hours: 24 * 3, reviews: 0 },
  { label: "7d", hours: 24 * 7, reviews: 0 },
  { label: "30d", hours: 24 * 30, reviews: 0 },
];

export const DEFAULT_HAZARD_BIN_EDGES_HOURS = [
  1 / 60,
  2 / 60,
  3 / 60,
  5 / 60,
  1 / 6,
  0.5,
  1,
  3,
  8,
  24,
  24 * 3,
  24 * 7,
  24 * 30,
];

export const REVIEW_COUNT_EFFECT_TAU = 4;
export const REVIEW_COUNT_EFFECT_SCALE = 0.35;
export const UNSEEN_RECALL_PRIOR_STRENGTH = 8;
export const UNSEEN_RECALL_PRIOR_DEFAULT = 0.45;

export const DEFAULT_STATE_FEATURE_NAMES = [
  "is_new",
  "last_correct",
  "log_successes",
  "correct_reviews",
  "log_failures",
  "success_balance",
  "log_total_reviews",
  "total_reviews",
  "recent_success_rate_3",
  "recent_success_rate_5",
  "recent_failure_rate_5",
  "recent_mean_log_latency_5",
  "recent_mean_log_gap_hours_5",
  "query_side_shown",
  "query_both_sides_mode",
  "query_side_last_correct",
  "log_query_side_successes",
  "log_query_side_failures",
  "query_side_success_balance",
  "log_query_side_elapsed_hours",
  "log_query_side_elapsed_reviews",
  "query_side_recent_success_rate_3",
  "query_side_recent_success_rate_5",
  "log_current_correct_streak",
  "log_current_incorrect_streak",
  "log_reviews_since_last_failure",
  "log_hours_since_last_failure",
  "recent_failures_5",
  "log_card_age_hours",
  "log_hours_since_first_correct",
  "log_reviews_per_day",
  "deck_running_accuracy",
  "deck_recent_success_rate_20",
  "deck_recent_success_rate_50",
  "deck_recent_mean_log_latency_20",
  "deck_recent_mean_log_gap_hours_20",
  "deck_recent_new_rate_20",
  "log_deck_total_reviews",
  "log_deck_seen_cards",
  "log_deck_age_hours",
  "log_deck_reviews_per_day",
  "query_side_elapsed_hours_capped_168",
  "query_side_elapsed_reviews_capped_64",
  "hours_since_last_failure_capped_168",
  "card_age_hours_capped_720",
  "hours_since_first_correct_capped_720",
  "reviews_per_day_capped_32",
  "deck_age_hours_capped_720",
  "deck_reviews_per_day_capped_64",
];

const toNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const toBinary = (value) => (value ? 1 : 0);

const safeLog1p = (value) => Math.log1p(Math.max(toNumber(value), 0));
const clampProbability = (value) =>
  Math.min(Math.max(toNumber(value, 0), 0), 1);

const clampUnitOpenProbability = (value) =>
  Math.min(Math.max(toNumber(value, 0), 1e-6), 1 - 1e-6);

const sigmoid = (value) => 1 / (1 + Math.exp(-toNumber(value, 0)));

const getBasename = (filename) => {
  if (typeof filename !== "string" || filename.trim().length === 0) {
    return null;
  }

  const parts = filename.replaceAll("\\", "/").split("/");
  return parts[parts.length - 1] || null;
};
const saturateCount = (value, saturationMax = null) => {
  const boundedValue = Math.max(toNumber(value), 0);
  const maxValue = toNumber(saturationMax, 0);
  if (!Number.isFinite(maxValue) || maxValue <= 0) {
    return boundedValue;
  }

  return maxValue * Math.tanh(boundedValue / maxValue);
};

const capValue = (value, maximum) =>
  Math.min(Math.max(toNumber(value), 0), maximum);

const reviewCountEffect = (cumulativeReviews) =>
  1 - Math.exp(-Math.max(toNumber(cumulativeReviews, 0), 0) / REVIEW_COUNT_EFFECT_TAU);

const getReviewId = (review) =>
  toNumber(review?.review_id, toNumber(review?.review, 0));

const getSubmittedTime = (review) => toNumber(review?.submitted_time, 0);

const getSideShown = (review) =>
  toBinary(review?.side_shown ?? review?.was_flipped);

const getBothSidesMode = (review) => toBinary(review?.both_sides_mode);

const getSourceId = (review) => toNumber(review?.source_id, 0);

const getResponseTimeSeconds = (review) => {
  const visibleTime = toNumber(review?.visible_time, 0);
  if (visibleTime > 0) {
    return visibleTime;
  }

  const showedTime = toNumber(review?.showed_time, 0);
  const submittedTime = getSubmittedTime(review);
  if (showedTime > 0 && submittedTime >= showedTime) {
    return submittedTime - showedTime;
  }

  return 0;
};

const getFlipLatencySeconds = (review) => {
  if (!toBinary(review?.was_flipped)) {
    return 0;
  }

  const showedTime = toNumber(review?.showed_time, 0);
  const flippedTime = toNumber(review?.flipped_time, 0);
  if (flippedTime > 0 && showedTime > 0 && flippedTime >= showedTime) {
    return flippedTime - showedTime;
  }

  return getResponseTimeSeconds(review);
};

const getGapSeconds = (review) => {
  if (review?.delta_t != null) {
    return Math.max(toNumber(review.delta_t), 0);
  }

  return 0;
};

const getGapReviews = (review) => {
  if (review?.delta_r != null) {
    return Math.max(toNumber(review.delta_r), 0);
  }

  return 0;
};

const getCorrectAfter = (review) =>
  toNumber(review?.correct, 0) + toBinary(review?.was_correct);

const getIncorrectAfter = (review) =>
  toNumber(review?.incorrect, 0) + (1 - toBinary(review?.was_correct));

const successBalance = (correct, incorrect) => {
  const total = correct + incorrect;
  if (total <= 0) {
    return 0;
  }

  return (correct - incorrect) / total;
};

const buildHistoryStepFeatures = (
  review,
  queryTime,
  recencyPosition,
  { includeUserResponseFeatures = true, reviewCountSaturationMax = null } = {}
) => {
  const correctAfter = getCorrectAfter(review);
  const incorrectAfter = getIncorrectAfter(review);
  const saturatedGapReviews = saturateCount(
    getGapReviews(review),
    reviewCountSaturationMax
  );
  const saturatedCorrectAfter = saturateCount(
    correctAfter,
    reviewCountSaturationMax
  );
  const saturatedIncorrectAfter = saturateCount(
    incorrectAfter,
    reviewCountSaturationMax
  );

  return [
    toBinary(review?.was_correct),
    includeUserResponseFeatures ? safeLog1p(getResponseTimeSeconds(review)) : 0,
    toBinary(review?.was_flipped),
    safeLog1p(getFlipLatencySeconds(review)),
    safeLog1p(getGapSeconds(review) / 3600),
    safeLog1p(saturatedGapReviews),
    safeLog1p(saturatedCorrectAfter),
    safeLog1p(saturatedIncorrectAfter),
    successBalance(correctAfter, incorrectAfter),
    getSideShown(review),
    getBothSidesMode(review),
    safeLog1p((queryTime - getSubmittedTime(review)) / 3600),
    recencyPosition,
  ];
};

const summarizeRecentHistory = (historyRows, windowSize) => {
  const windowRows = historyRows.slice(-windowSize);
  if (windowRows.length === 0) {
    return {
      successRate: 0,
      failureRate: 0,
      meanLogLatency: 0,
      meanLogGapHours: 0,
    };
  }

  const successes = windowRows.reduce(
    (sum, review) => sum + toBinary(review?.was_correct),
    0
  );
  const failures = windowRows.length - successes;

  return {
    successRate: successes / windowRows.length,
    failureRate: failures / windowRows.length,
    meanLogLatency:
      windowRows.reduce(
        (sum, review) => sum + safeLog1p(getResponseTimeSeconds(review)),
        0
      ) / windowRows.length,
    meanLogGapHours:
      windowRows.reduce(
        (sum, review) => sum + safeLog1p(getGapSeconds(review) / 3600),
        0
      ) / windowRows.length,
  };
};

const getRecentFailureCount = (historyRows, windowSize) =>
  historyRows
    .slice(-windowSize)
    .reduce((sum, review) => sum + (1 - toBinary(review?.was_correct)), 0);

const getTrailingStreak = (historyRows, targetCorrect) => {
  let streak = 0;
  const targetValue = targetCorrect ? 1 : 0;
  for (let index = historyRows.length - 1; index >= 0; index -= 1) {
    if (toBinary(historyRows[index]?.was_correct) !== targetValue) {
      break;
    }
    streak += 1;
  }
  return streak;
};

const findLastReview = (historyRows, predicate) => {
  for (let index = historyRows.length - 1; index >= 0; index -= 1) {
    if (predicate(historyRows[index])) {
      return historyRows[index];
    }
  }
  return null;
};

const findFirstReview = (historyRows, predicate) => {
  for (let index = 0; index < historyRows.length; index += 1) {
    if (predicate(historyRows[index])) {
      return historyRows[index];
    }
  }
  return null;
};

export const getReviewsSinceLastFailureCount = (historyRows) => {
  const lastFailureReview = findLastReview(
    historyRows,
    (review) => !toBinary(review?.was_correct)
  );
  return lastFailureReview
    ? historyRows.length - historyRows.lastIndexOf(lastFailureReview) - 1
    : historyRows.length;
};

const sortHistoryRows = (historyRows) =>
  [...historyRows].sort((left, right) => {
    const reviewGap = getReviewId(left) - getReviewId(right);
    if (reviewGap !== 0) {
      return reviewGap;
    }

    return getSubmittedTime(left) - getSubmittedTime(right);
  });

const createEmptyRecentWindowSummary = () => ({
  count: 0,
  successes: 0,
  failures: 0,
  sumLogLatency: 0,
  sumLogGapHours: 0,
  sumIsNew: 0,
  rows: [],
});

const addReviewToRecentWindowSummary = (summary, review, windowSize) => {
  const nextRows = [...summary.rows, review];
  let count = summary.count + 1;
  let successes = summary.successes + toBinary(review?.was_correct);
  let failures = summary.failures + (1 - toBinary(review?.was_correct));
  let sumLogLatency =
    summary.sumLogLatency + safeLog1p(getResponseTimeSeconds(review));
  let sumLogGapHours =
    summary.sumLogGapHours + safeLog1p(getGapSeconds(review) / 3600);
  let sumIsNew = summary.sumIsNew + toBinary(review?.is_new);

  if (nextRows.length > windowSize) {
    const droppedReview = nextRows.shift();
    count -= 1;
    successes -= toBinary(droppedReview?.was_correct);
    failures -= 1 - toBinary(droppedReview?.was_correct);
    sumLogLatency -= safeLog1p(getResponseTimeSeconds(droppedReview));
    sumLogGapHours -= safeLog1p(getGapSeconds(droppedReview) / 3600);
    sumIsNew -= toBinary(droppedReview?.is_new);
  }

  return {
    count,
    successes,
    failures,
    sumLogLatency,
    sumLogGapHours,
    sumIsNew,
    rows: nextRows,
  };
};

const summarizeRecentWindow = (summary) => {
  if (!summary || summary.count === 0) {
    return {
      successRate: 0,
      failureRate: 0,
      meanLogLatency: 0,
      meanLogGapHours: 0,
      newRate: 0,
    };
  }

  return {
    successRate: summary.successes / summary.count,
    failureRate: summary.failures / summary.count,
    meanLogLatency: summary.sumLogLatency / summary.count,
    meanLogGapHours: summary.sumLogGapHours / summary.count,
    newRate: summary.sumIsNew / summary.count,
  };
};

export const summarizeDeckHistoryRows = (historyRows) => {
  const sortedRows = sortHistoryRows(historyRows);
  if (sortedRows.length === 0) {
    return {
      totalReviews: 0,
      successes: 0,
      newReviewCount: 0,
      newSuccesses: 0,
      firstSubmittedTime: 0,
      seenCardCount: 0,
      seenCardIds: new Set(),
      recent20: createEmptyRecentWindowSummary(),
      recent50: createEmptyRecentWindowSummary(),
    };
  }

  let recent20 = createEmptyRecentWindowSummary();
  let recent50 = createEmptyRecentWindowSummary();
  let successes = 0;
  const seenCardIds = new Set();

  sortedRows.forEach((review) => {
      successes += toBinary(review?.was_correct);
    seenCardIds.add(Number(review?.card_id));
    recent20 = addReviewToRecentWindowSummary(recent20, review, 20);
    recent50 = addReviewToRecentWindowSummary(recent50, review, 50);
  });

  const newReviews = sortedRows.filter((review) => toBinary(review?.is_new));
  const newSuccesses = newReviews.reduce(
    (sum, review) => sum + toBinary(review?.was_correct),
    0
  );

  return {
    totalReviews: sortedRows.length,
    successes,
    newReviewCount: newReviews.length,
    newSuccesses,
    firstSubmittedTime: getSubmittedTime(sortedRows[0]),
    seenCardCount: seenCardIds.size,
    seenCardIds,
    recent20,
    recent50,
  };
};

export const extendDeckSummary = (deckSummary, review) => {
  const baseSummary = deckSummary ?? summarizeDeckHistoryRows([]);
  const cardId = Number(review?.card_id);
  const nextSeenCardIds = new Set(baseSummary.seenCardIds ?? []);
  const seenCardCount = nextSeenCardIds.has(cardId)
    ? baseSummary.seenCardCount
    : baseSummary.seenCardCount + 1;
  nextSeenCardIds.add(cardId);

  return {
    totalReviews: baseSummary.totalReviews + 1,
    successes: baseSummary.successes + toBinary(review?.was_correct),
    newReviewCount:
      baseSummary.newReviewCount + toBinary(review?.is_new),
    newSuccesses:
      baseSummary.newSuccesses +
      (toBinary(review?.is_new) ? toBinary(review?.was_correct) : 0),
    firstSubmittedTime:
      baseSummary.totalReviews > 0
        ? baseSummary.firstSubmittedTime
        : getSubmittedTime(review),
    seenCardCount,
    seenCardIds: nextSeenCardIds,
    recent20: addReviewToRecentWindowSummary(baseSummary.recent20, review, 20),
    recent50: addReviewToRecentWindowSummary(baseSummary.recent50, review, 50),
  };
};

export const buildSequenceInputs = (
  historyRows,
  queryTime,
  maxHistory,
  sequenceFeatureCount,
  historyWindowSize = maxHistory,
  reviewCountSaturationMax = null
) => {
  const effectiveHistoryWindow = Math.max(
    0,
    Math.min(toNumber(historyWindowSize, maxHistory), maxHistory)
  );
  const trimmed =
    effectiveHistoryWindow > 0 ? historyRows.slice(-effectiveHistoryWindow) : [];
  const sequenceFeatures = Array.from({ length: maxHistory }, () =>
    new Array(sequenceFeatureCount).fill(0)
  );
  const sequenceMask = new Array(maxHistory).fill(0);

  trimmed.forEach((review, index) => {
    sequenceFeatures[index] = buildHistoryStepFeatures(
      review,
      queryTime,
      (index + 1) / Math.max(trimmed.length, 1),
      {
        includeUserResponseFeatures: index < trimmed.length - 1,
        reviewCountSaturationMax,
      }
    );
    sequenceMask[index] = 1;
  });

  return { sequenceFeatures, sequenceMask };
};

export const getEffectiveInferenceHistoryWindow = (metadata = {}) => {
  const maxHistory = Math.max(0, Math.floor(toNumber(metadata?.max_history, 0)));
  const metadataWindow = metadata?.inference_history_window;
  if (metadataWindow == null) {
    return maxHistory;
  }

  return Math.max(
    0,
    Math.min(maxHistory, Math.floor(toNumber(metadataWindow, maxHistory)))
  );
};

export const getReviewCountSaturationMax = (metadata = {}) => {
  const saturationMax = toNumber(metadata?.review_count_saturation_max, 0);
  return Number.isFinite(saturationMax) && saturationMax > 0
    ? saturationMax
    : null;
};

export const isCalibrationCompatibleWithMetadata = (
  metadata = {},
  calibration = null
) => {
  if (!calibration || typeof calibration !== "object") {
    return false;
  }

  const calibrationSource = getBasename(calibration.source_model);
  if (!calibrationSource) {
    return true;
  }

  const metadataSource = getBasename(metadata?.output_onnx);
  if (!metadataSource) {
    return true;
  }

  return calibrationSource === metadataSource;
};

export const buildStateFeatures = (
  historyRows,
  queryContext = {},
  featureNames = DEFAULT_STATE_FEATURE_NAMES
) => {
  const deckSummary =
    queryContext?.deckSummary ??
    summarizeDeckHistoryRows(queryContext?.deckHistoryRows ?? historyRows);
  const querySideShown = toBinary(queryContext?.sideShown);
  const queryBothSidesMode =
    queryContext?.bothSidesMode == null
      ? 1
      : toBinary(queryContext?.bothSidesMode);
  const reviewCountSaturationMax =
    getReviewCountSaturationMax(queryContext) ??
    getReviewCountSaturationMax(queryContext?.metadata);
  const querySideHistory = historyRows.filter(
    (review) => getSideShown(review) === querySideShown
  );
  const featureValues = {
    is_new: historyRows.length === 0 ? 1 : 0,
    last_correct: 0,
    log_successes: 0,
    correct_reviews: 0,
    log_failures: 0,
    success_balance: 0,
    log_total_reviews: 0,
    total_reviews: 0,
    recent_success_rate_3: 0,
    recent_success_rate_5: 0,
    recent_failure_rate_5: 0,
    recent_mean_log_latency_5: 0,
    recent_mean_log_gap_hours_5: 0,
    query_side_shown: querySideShown,
    query_both_sides_mode: queryBothSidesMode,
    query_side_last_correct: 0,
    log_query_side_successes: 0,
    log_query_side_failures: 0,
    query_side_success_balance: 0,
    log_query_side_elapsed_hours: 0,
    log_query_side_elapsed_reviews: 0,
    query_side_recent_success_rate_3: 0,
    query_side_recent_success_rate_5: 0,
    log_current_correct_streak: 0,
    log_current_incorrect_streak: 0,
    log_reviews_since_last_failure: 0,
    log_hours_since_last_failure: 0,
    recent_failures_5: 0,
    log_card_age_hours: 0,
    log_hours_since_first_correct: 0,
    log_reviews_per_day: 0,
    deck_running_accuracy: 0,
    deck_recent_success_rate_20: 0,
    deck_recent_success_rate_50: 0,
    deck_recent_mean_log_latency_20: 0,
    deck_recent_mean_log_gap_hours_20: 0,
    deck_recent_new_rate_20: 0,
    log_deck_total_reviews: 0,
    log_deck_seen_cards: 0,
    log_deck_age_hours: 0,
    log_deck_reviews_per_day: 0,
    query_side_elapsed_hours_capped_168: 0,
    query_side_elapsed_reviews_capped_64: 0,
    hours_since_last_failure_capped_168: 0,
    card_age_hours_capped_720: 0,
    hours_since_first_correct_capped_720: 0,
    reviews_per_day_capped_32: 0,
    deck_age_hours_capped_720: 0,
    deck_reviews_per_day_capped_64: 0,
  };

  const queryTime = toNumber(
    queryContext?.queryTime,
    historyRows.length > 0 ? getSubmittedTime(historyRows[historyRows.length - 1]) : 0
  );

  if (historyRows.length > 0) {
    const lastReview = historyRows[historyRows.length - 1];
    const correct = getCorrectAfter(lastReview);
    const incorrect = getIncorrectAfter(lastReview);
    const totalReviews = correct + incorrect;
    const saturatedCorrect = saturateCount(correct, reviewCountSaturationMax);
    const saturatedIncorrect = saturateCount(
      incorrect,
      reviewCountSaturationMax
    );
    const saturatedTotalReviews = saturateCount(
      totalReviews,
      reviewCountSaturationMax
    );
    const recentThree = summarizeRecentHistory(historyRows, 3);
    const recentFive = summarizeRecentHistory(historyRows, 5);
    const lastSideReview = querySideHistory[querySideHistory.length - 1];
    const sideCorrect = lastSideReview ? getCorrectAfter(lastSideReview) : 0;
    const sideIncorrect = lastSideReview ? getIncorrectAfter(lastSideReview) : 0;
    const sideRecentThree = summarizeRecentHistory(querySideHistory, 3);
    const sideRecentFive = summarizeRecentHistory(querySideHistory, 5);
    const sideElapsedHours = lastSideReview
      ? Math.max(queryTime - getSubmittedTime(lastSideReview), 0) / 3600
      : 0;
    const queryReviewIndex = toNumber(
      queryContext?.queryReviewIndex,
      getReviewId(lastReview)
    );
    const saturatedQueryReviewIndex = queryReviewIndex;
    const sideElapsedReviews = lastSideReview
      ? Math.max(saturatedQueryReviewIndex - getReviewId(lastSideReview), 0)
      : 0;
    const saturatedSideCorrect = saturateCount(
      sideCorrect,
      reviewCountSaturationMax
    );
    const saturatedSideIncorrect = saturateCount(
      sideIncorrect,
      reviewCountSaturationMax
    );
    const saturatedSideElapsedReviews = saturateCount(
      sideElapsedReviews,
      reviewCountSaturationMax
    );
    const lastFailureReview = findLastReview(
      historyRows,
      (review) => !toBinary(review?.was_correct)
    );
    const firstReview = historyRows[0];
    const firstCorrectReview = findFirstReview(
      historyRows,
      (review) => toBinary(review?.was_correct)
    );
    const reviewsSinceLastFailure = getReviewsSinceLastFailureCount(historyRows);
    const saturatedReviewsSinceLastFailure = saturateCount(
      reviewsSinceLastFailure,
      reviewCountSaturationMax
    );
    const hoursSinceLastFailure = lastFailureReview
      ? Math.max(queryTime - getSubmittedTime(lastFailureReview), 0) / 3600
      : Math.max(queryTime - getSubmittedTime(firstReview), 0) / 3600;
    const cardAgeHours =
      Math.max(queryTime - getSubmittedTime(firstReview), 0) / 3600;
    const hoursSinceFirstCorrect = firstCorrectReview
      ? Math.max(queryTime - getSubmittedTime(firstCorrectReview), 0) / 3600
      : 0;
    const reviewsPerDay = totalReviews / Math.max(cardAgeHours / 24, 1);
    const saturatedCorrectStreak = saturateCount(
      getTrailingStreak(historyRows, true),
      reviewCountSaturationMax
    );
    const saturatedIncorrectStreak = saturateCount(
      getTrailingStreak(historyRows, false),
      reviewCountSaturationMax
    );

    featureValues.is_new = 0;
    featureValues.last_correct = toBinary(lastReview?.was_correct);
    featureValues.log_successes = safeLog1p(saturatedCorrect);
    featureValues.correct_reviews = saturatedCorrect;
    featureValues.log_failures = safeLog1p(saturatedIncorrect);
    featureValues.success_balance = successBalance(correct, incorrect);
    featureValues.log_total_reviews = safeLog1p(saturatedTotalReviews);
    featureValues.total_reviews = saturatedTotalReviews;
    featureValues.recent_success_rate_3 = recentThree.successRate;
    featureValues.recent_success_rate_5 = recentFive.successRate;
    featureValues.recent_failure_rate_5 = recentFive.failureRate;
    featureValues.recent_mean_log_latency_5 = recentFive.meanLogLatency;
    featureValues.recent_mean_log_gap_hours_5 = recentFive.meanLogGapHours;
    featureValues.query_side_last_correct = toBinary(lastSideReview?.was_correct);
    featureValues.log_query_side_successes = safeLog1p(saturatedSideCorrect);
    featureValues.log_query_side_failures = safeLog1p(saturatedSideIncorrect);
    featureValues.query_side_success_balance = successBalance(
      sideCorrect,
      sideIncorrect
    );
    featureValues.log_query_side_elapsed_hours = safeLog1p(sideElapsedHours);
    featureValues.log_query_side_elapsed_reviews = safeLog1p(
      saturatedSideElapsedReviews
    );
    featureValues.query_side_recent_success_rate_3 = sideRecentThree.successRate;
    featureValues.query_side_recent_success_rate_5 = sideRecentFive.successRate;
    featureValues.log_current_correct_streak = safeLog1p(saturatedCorrectStreak);
    featureValues.log_current_incorrect_streak = safeLog1p(
      saturatedIncorrectStreak
    );
    featureValues.log_reviews_since_last_failure = safeLog1p(
      saturatedReviewsSinceLastFailure
    );
    featureValues.log_hours_since_last_failure = safeLog1p(
      hoursSinceLastFailure
    );
    featureValues.recent_failures_5 = getRecentFailureCount(historyRows, 5);
    featureValues.log_card_age_hours = safeLog1p(cardAgeHours);
    featureValues.log_hours_since_first_correct = safeLog1p(
      hoursSinceFirstCorrect
    );
    featureValues.log_reviews_per_day = safeLog1p(reviewsPerDay);
    featureValues.query_side_elapsed_hours_capped_168 = capValue(
      sideElapsedHours,
      168
    );
    featureValues.query_side_elapsed_reviews_capped_64 = capValue(
      saturatedSideElapsedReviews,
      64
    );
    featureValues.hours_since_last_failure_capped_168 = capValue(
      hoursSinceLastFailure,
      168
    );
    featureValues.card_age_hours_capped_720 = capValue(cardAgeHours, 720);
    featureValues.hours_since_first_correct_capped_720 = capValue(
      hoursSinceFirstCorrect,
      720
    );
    featureValues.reviews_per_day_capped_32 = capValue(reviewsPerDay, 32);
  }

  if ((deckSummary?.totalReviews ?? 0) > 0) {
    const deckRecentTwenty = summarizeRecentWindow(deckSummary.recent20);
    const deckRecentFifty = summarizeRecentWindow(deckSummary.recent50);
    const deckTotalReviews = deckSummary.totalReviews;
    const saturatedDeckTotalReviews = saturateCount(
      deckTotalReviews,
      reviewCountSaturationMax
    );
    const deckSuccesses = deckSummary.successes;
    const deckSeenCards = deckSummary.seenCardCount;
    const saturatedDeckSeenCards = saturateCount(
      deckSeenCards,
      reviewCountSaturationMax
    );
    const deckAgeHours =
      Math.max(queryTime - deckSummary.firstSubmittedTime, 0) / 3600;
    const deckReviewsPerDay = deckTotalReviews / Math.max(deckAgeHours / 24, 1);

    featureValues.deck_running_accuracy = deckSuccesses / deckTotalReviews;
    featureValues.deck_recent_success_rate_20 = deckRecentTwenty.successRate;
    featureValues.deck_recent_success_rate_50 = deckRecentFifty.successRate;
    featureValues.deck_recent_mean_log_latency_20 =
      deckRecentTwenty.meanLogLatency;
    featureValues.deck_recent_mean_log_gap_hours_20 =
      deckRecentTwenty.meanLogGapHours;
    featureValues.deck_recent_new_rate_20 = deckRecentTwenty.newRate;
    featureValues.log_deck_total_reviews = safeLog1p(saturatedDeckTotalReviews);
    featureValues.log_deck_seen_cards = safeLog1p(saturatedDeckSeenCards);
    featureValues.log_deck_age_hours = safeLog1p(deckAgeHours);
    featureValues.log_deck_reviews_per_day = safeLog1p(deckReviewsPerDay);
    featureValues.deck_age_hours_capped_720 = capValue(deckAgeHours, 720);
    featureValues.deck_reviews_per_day_capped_64 = capValue(
      deckReviewsPerDay,
      64
    );
  }

  return featureNames.map((featureName) => featureValues[featureName] ?? 0);
};

export const buildDelayFeatures = (
  historyRows,
  queryTime,
  queryReviewIndex,
  reviewCountSaturationMax = null
) => {
  if (historyRows.length === 0) {
    return [0, 0];
  }

  const lastReview = historyRows[historyRows.length - 1];
  return [
    Math.max(queryTime - getSubmittedTime(lastReview), 0) / 3600,
    saturateCount(
      Math.max(queryReviewIndex - getReviewId(lastReview), 0),
      reviewCountSaturationMax
    ),
  ];
};

export const normalizeDelayQueries = (delayQueries = DEFAULT_DELAY_QUERIES) =>
  delayQueries.map((query, index) => {
    if (typeof query === "number") {
      return {
        label: `${query}h`,
        hours: query,
        reviews: 0,
        index,
      };
    }

    return {
      label: query.label ?? `${toNumber(query.hours, 0)}h`,
      hours: toNumber(query.hours, 0),
      reviews: toNumber(query.reviews, 0),
      index,
    };
  });

const normalizeVector = (values, stats) =>
  values.map(
    (value, index) => (value - stats.mean[index]) / (stats.scale[index] || 1)
  );

const normalizeSequence = (sequenceFeatures, sequenceMask, stats) =>
  sequenceFeatures.map((row, rowIndex) =>
    sequenceMask[rowIndex]
      ? normalizeVector(row, stats)
      : new Array(row.length).fill(0)
  );

const flatten2d = (rows) => rows.flat();
const flatten3d = (blocks) => blocks.flat(2);

const getHazardBinEdgesHours = (metadata) =>
  (metadata?.hazard_bin_edges_hours ?? DEFAULT_HAZARD_BIN_EDGES_HOURS).map(
    (value) => toNumber(value, 0)
  );

export const estimateUnseenRecallPrior = (deckSummary = null) => {
  const newReviewCount = toNumber(deckSummary?.newReviewCount, 0);
  const newSuccesses = toNumber(deckSummary?.newSuccesses, 0);

  return clampProbability(
    (newSuccesses +
      UNSEEN_RECALL_PRIOR_STRENGTH * UNSEEN_RECALL_PRIOR_DEFAULT) /
      (newReviewCount + UNSEEN_RECALL_PRIOR_STRENGTH)
  );
};

export const getCardHistory = (history, cardId) =>
  history[String(cardId)] ?? history[cardId] ?? [];

export const getDeckHistory = (history, sourceId = null) =>
  sortHistoryRows(
    Object.values(history)
      .flat()
      .filter((review) => sourceId == null || getSourceId(review) === sourceId)
  );

export const buildDeckSummaryIndex = (history) => {
  const allRows = [];
  const rowsBySource = new Map();

  Object.values(history).forEach((historyRows) => {
    historyRows.forEach((review) => {
      allRows.push(review);
      const sourceId = getSourceId(review);
      const sourceRows = rowsBySource.get(sourceId) ?? [];
      sourceRows.push(review);
      rowsBySource.set(sourceId, sourceRows);
    });
  });

  const allRowsSorted = sortHistoryRows(allRows);
  const rowsBySourceSorted = new Map(
    [...rowsBySource.entries()].map(([sourceId, sourceRows]) => [
      sourceId,
      sortHistoryRows(sourceRows),
    ])
  );

  return {
    allRows: allRowsSorted,
    allSummary: summarizeDeckHistoryRows(allRowsSorted),
    rowsBySource: rowsBySourceSorted,
    summariesBySource: new Map(
      [...rowsBySourceSorted.entries()].map(([sourceId, sourceRows]) => [
        sourceId,
        summarizeDeckHistoryRows(sourceRows),
      ])
    ),
  };
};

const buildCurveExamples = ({
  cards,
  history,
  now,
  reviewStep,
  delayQueries,
  metadata,
  sideShown = false,
  bothSidesMode = true,
}) =>
  (() => {
    const deckHistoryIndex = buildDeckSummaryIndex(history);
    const reviewCountSaturationMax = getReviewCountSaturationMax(metadata);

    return cards.flatMap((card) => {
      const cardId = Number(card.card_id);
      const historyRows = getCardHistory(history, cardId);
      const sourceId =
        historyRows.length > 0
          ? getSourceId(historyRows[historyRows.length - 1])
          : null;
      const deckSummary =
        sourceId == null
          ? deckHistoryIndex.allSummary
          : deckHistoryIndex.summariesBySource.get(sourceId) ??
            deckHistoryIndex.allSummary;

      return delayQueries.map((delayQuery) => {
        const queryTime = now + delayQuery.hours * 3600;
        const lastReviewId =
          historyRows.length > 0
            ? getReviewId(historyRows[historyRows.length - 1])
            : reviewStep;
        const queryReviewIndex = lastReviewId + delayQuery.reviews;
        const { sequenceFeatures, sequenceMask } = buildSequenceInputs(
          historyRows,
          queryTime,
          metadata.max_history,
          metadata.sequence_feature_names.length,
          getEffectiveInferenceHistoryWindow(metadata),
          reviewCountSaturationMax
        );
        const stateFeatures = buildStateFeatures(
          historyRows,
          {
            sideShown,
            bothSidesMode,
            queryTime,
            queryReviewIndex,
            deckSummary,
            reviewCountSaturationMax,
          },
          metadata.state_feature_names
        );

        return {
          cardId,
          historyRows,
          deckSummary,
          delayQuery,
          sequenceFeatures,
          sequenceMask,
          stateFeatures,
          delayFeatures: buildDelayFeatures(
            historyRows,
            queryTime,
            queryReviewIndex,
            reviewCountSaturationMax
          ),
        };
      });
    });
  })();

export const predictForgettingCurveBatch = async (
  session,
  metadata,
  examples,
  calibration = null
) => {
  const normalizedSequence = examples.map((example) =>
    normalizeSequence(
      example.sequenceFeatures,
      example.sequenceMask,
      metadata.sequence_feature_stats
    )
  );
  const normalizedState = examples.map((example) =>
    normalizeVector(example.stateFeatures, metadata.state_feature_stats)
  );
  const sequenceMask = examples.map((example) => example.sequenceMask);

  const feeds = {
    sequence_features: new onnx.Tensor(
      "float32",
      Float32Array.from(flatten3d(normalizedSequence)),
      [
        examples.length,
        metadata.max_history,
        metadata.sequence_feature_names.length,
      ]
    ),
    sequence_mask: new onnx.Tensor(
      "float32",
      Float32Array.from(flatten2d(sequenceMask)),
      [examples.length, metadata.max_history]
    ),
    state_features: new onnx.Tensor(
      "float32",
      Float32Array.from(flatten2d(normalizedState)),
      [examples.length, metadata.state_feature_names.length]
    ),
  };

  const output = await session.run(feeds);
  const modelType =
    typeof metadata?.model_type === "string"
      ? metadata.model_type
      : "discrete_hazard_forgetting_curve";
  const hazardBinEdgesHours =
    modelType === "discrete_hazard_forgetting_curve"
      ? getHazardBinEdgesHours(metadata)
      : null;
  const baseHazardLogits =
    modelType === "discrete_hazard_forgetting_curve"
      ? Array.from(output.base_hazard_logit.data)
      : null;
  const reviewSensitivities =
    modelType === "discrete_hazard_forgetting_curve"
      ? Array.from(output.review_sensitivity.data)
      : null;
  const hazardCount = hazardBinEdgesHours?.length ?? 0;
  const rawParameters =
    modelType === "neural_half_life_regression_exponential" ||
    modelType === "neural_half_life_regression_weibull"
      ? Array.from(output.raw_parameters.data)
      : null;
  const parameterCount =
    modelType === "neural_half_life_regression_weibull"
      ? 2
      : modelType === "neural_half_life_regression_exponential"
        ? 1
        : 0;
  const predictions = examples.map((example, index) => {
    if (
      example.historyRows.length === 0 &&
      Math.abs(example.delayFeatures[0]) <= 1e-9 &&
      Math.abs(example.delayFeatures[1]) <= 1e-9
    ) {
      return estimateUnseenRecallPrior(example.deckSummary);
    }

    if (modelType === "discrete_hazard_forgetting_curve") {
      const start = index * hazardCount;
      const hazardLogits = baseHazardLogits.slice(start, start + hazardCount);
      const sensitivity = reviewSensitivities.slice(start, start + hazardCount);
      return hazardProbabilitiesToRecallProbability(
        hazardLogits,
        sensitivity,
        example.delayFeatures[0],
        example.delayFeatures[1],
        hazardBinEdgesHours
      );
    }

    if (
      modelType === "neural_half_life_regression_exponential" ||
      modelType === "neural_half_life_regression_weibull"
    ) {
      const start = index * parameterCount;
      return rawParametersToRecallProbability(
        rawParameters.slice(start, start + parameterCount),
        example.delayFeatures[0],
        metadata
      );
    }

    throw new Error(`Unsupported forgetting-curve model_type: ${modelType}`);
  });

  if (!isCalibrationCompatibleWithMetadata(metadata, calibration)) {
    return predictions;
  }

  return applyIsotonicCalibrationBatch(
    predictions,
    calibration,
    examples.map((example) =>
      getReviewsSinceLastFailureCount(example.historyRows)
    )
  );
};

export function rawParametersToRecallProbability(
  rawParameters,
  elapsedHours,
  metadata = {}
) {
  const totalHours = Math.max(toNumber(elapsedHours, 0), 0);
  if (totalHours <= 0) {
    return 1;
  }

  const modelType =
    typeof metadata?.model_type === "string"
      ? metadata.model_type
      : "neural_half_life_regression_exponential";
  const halfLifeSpec = metadata?.parameterization?.half_life_hours ?? {};
  const halfLifeMin = Math.max(toNumber(halfLifeSpec.min, 1 / 60), 1e-6);
  const halfLifeMax = Math.max(toNumber(halfLifeSpec.max, 24 * 365 * 5), halfLifeMin);
  const halfLifeHours =
    halfLifeMin +
    (halfLifeMax - halfLifeMin) * sigmoid(rawParameters?.[0]);
  const scaledDelay = totalHours / Math.max(halfLifeHours, 1e-6);

  let logSurvival = -Math.log(2) * scaledDelay;
  if (modelType === "neural_half_life_regression_weibull") {
    const shapeSpec = metadata?.parameterization?.weibull_shape ?? {};
    const shapeMin = Math.max(toNumber(shapeSpec.min, 0.25), 1e-6);
    const shapeMax = Math.max(toNumber(shapeSpec.max, 4), shapeMin);
    const shape =
      shapeMin + (shapeMax - shapeMin) * sigmoid(rawParameters?.[1]);
    logSurvival =
      -Math.log(2) * Math.pow(Math.max(scaledDelay, 1e-6), shape);
  }

  return clampUnitOpenProbability(Math.exp(logSurvival));
}

export function hazardProbabilitiesToRecallProbability(
  baseHazardLogits,
  reviewSensitivities,
  elapsedHours,
  elapsedReviews = 0,
  hazardBinEdgesHours = DEFAULT_HAZARD_BIN_EDGES_HOURS
) {
  const totalHours = Math.max(toNumber(elapsedHours, 0), 0);
  if (totalHours <= 0) {
    return 1;
  }

  let survivalLog = 0;
  for (let index = 0; index < hazardBinEdgesHours.length; index += 1) {
    const binEnd = hazardBinEdgesHours[index];
    const binStart = index === 0 ? 0 : hazardBinEdgesHours[index - 1];
    if (totalHours <= binStart) {
      break;
    }

    const coveredHours = Math.min(totalHours, binEnd) - binStart;
    const binWidth = Math.max(binEnd - binStart, 1e-6);
    if (coveredHours <= 0) {
      continue;
    }

    const cumulativeReviews =
      Math.max(toNumber(elapsedReviews, 0), 0) *
      Math.min(Math.max(binEnd / totalHours, 0), 1);
    const baseLogit = toNumber(baseHazardLogits[index], 0);
    const reviewSensitivity = Math.max(
      toNumber(reviewSensitivities[index], 0),
      0
    );
    const countEffect = reviewCountEffect(cumulativeReviews);
    const hazard = Math.min(
      Math.max(
        1 /
          (1 +
            Math.exp(
              -(
                baseLogit +
                reviewSensitivity *
                  REVIEW_COUNT_EFFECT_SCALE *
                  countEffect
              )
            )),
        1e-6
      ),
      1 - 1e-6
    );
    survivalLog += Math.log1p(-hazard) * (coveredHours / binWidth);

    if (totalHours <= binEnd) {
      break;
    }
  }

  return Math.exp(survivalLog);
}

export const predictRecallCurvesForCards = async (
  session,
  metadata,
  cards,
  history,
  delayQueries = DEFAULT_DELAY_QUERIES,
  options = {}
) => {
  const normalizedDelayQueries = normalizeDelayQueries(delayQueries);
  const examples = buildCurveExamples({
    cards,
    history,
    now: options.now ?? Date.now() / 1000,
    reviewStep: options.reviewStep ?? 0,
    delayQueries: normalizedDelayQueries,
    metadata,
    sideShown: options.sideShown ?? false,
    bothSidesMode: options.bothSidesMode ?? true,
  });
  const predictions = await predictForgettingCurveBatch(
    session,
    metadata,
    examples,
    options.calibration ?? null
  );

  return cards.reduce((curvesByCardId, card, cardIndex) => {
    const start = cardIndex * normalizedDelayQueries.length;
    curvesByCardId[Number(card.card_id)] = normalizedDelayQueries.map(
      (delayQuery, delayIndex) => ({
        ...delayQuery,
        probability: predictions[start + delayIndex],
      })
    );
    return curvesByCardId;
  }, {});
};
